import { emit } from '@zapier/events-toolkit-browser';
import { useABTestContext } from '../../components/ABTest/ABTest';
import { useSplitTestContext } from '../../components/SplitTest/SplitTest';

export enum Event {
  UserInteraction = 'web_analytics.tracking.user_interaction.UserInteractionEvent',
  PageView = 'web_analytics.tracking.page_view.PageViewEvent',
}

export enum EventAction {
  click = 'click',
  in_view = 'in_view',
}

export enum InteractionName {
  call_to_action = 'call_to_action',
  carousel_button_click = 'carousel_button_click',
  component_scrolled_into_view = 'component_scrolled_into_view',
  navigation = 'navigation',
  tab_change = 'tab_change',
  dismiss = 'dismiss',
  toggle = 'toggle',
  video = 'video',
}

export interface EventDetails {
  custom_tracking_fields?: Record<string, any>;
  destination_href?: string;
  event_action: EventAction;
  experiment_flag: boolean;
  experiment_name?: string;
  interaction_name: InteractionName;
  interaction_raw_text?: string;
  option_selected?: string;
}

export enum CTAType {
  block_grid_item_link = 'block_grid_item_link',
  cta_button = 'cta_button',
  cta_link = 'cta_link',
  zap_card_link = 'zap_card_link',
}

export interface SectionEventDetails {
  experiment_flag: boolean;
  experiment_name?: string;
  section: string;
}

export interface AccordionToggleEventDetails extends SectionEventDetails {
  title: string;
  option_selected: 'opening' | 'closing';
}

export interface CTAClickEventDetails extends SectionEventDetails {
  text: string;
  href: string;
  type?: CTAType;
}

export function emitPageViewEvent() {
  return emit({ system_name: 'pagebuilder' }, Event.PageView);
}

export function emitUserInteractionEvent(eventDetails: EventDetails) {
  return emit(
    {
      ...eventDetails,
      system_name: 'pagebuilder',
    },
    Event.UserInteraction,
  );
}

export function emitCTAClickEvent({
  text,
  href,
  type = CTAType.cta_button,
  section,
  experiment_name,
  experiment_flag,
}: CTAClickEventDetails) {
  return emitUserInteractionEvent({
    experiment_flag,
    experiment_name,
    event_action: EventAction.click,
    interaction_name: InteractionName.call_to_action,
    interaction_raw_text: text,
    destination_href: href,
    option_selected: type,
    custom_tracking_fields: {
      section_name: section,
    },
  });
}

export function emitAccordionToggleEvent({
  section,
  title,
  option_selected,
  experiment_name,
  experiment_flag,
}: AccordionToggleEventDetails) {
  return emitUserInteractionEvent({
    experiment_flag,
    experiment_name,
    event_action: EventAction.click,
    interaction_name: InteractionName.toggle,
    interaction_raw_text: title,
    option_selected,
    custom_tracking_fields: {
      section_name: section,
    },
  });
}

export function useAvroTracking() {
  const { isReady, splitName, treatment } = useSplitTestContext();
  const { experimentName, activeVariant } = useABTestContext();

  const injectExperimentContext =
    <T extends (...args: any) => any>(callback: T) =>
    (props: Omit<Parameters<T>[0], 'experiment_flag' | 'experiment_name'>) => {
      const isSplit = !!splitName;
      // If the activeVariant is a fallback, then the ABTest was not set up correctly and we do not send tracking events.
      const isValidABTest = !!activeVariant?.name && !activeVariant?.isFallback;

      const experiment_flag = isSplit || isValidABTest;

      const experiment_name = isSplit
        ? `${splitName}_${treatment}`
        : `${experimentName}_${activeVariant?.name}`;

      /**
       * Return early if there is a split experiment, but Split is not ready yet.
       * This happens in the tracking wrapper when the loading spinner is shown while waiting on Split and an 'in_view' event is fired.
       */
      if (isSplit && !isReady && experiment_flag) return () => {};

      return callback({
        ...props,
        ...(experiment_flag && {
          experiment_name,
        }),
        experiment_flag,
      });
    };

  return {
    emitPageViewEvent,
    emitCTAClickEvent: injectExperimentContext(emitCTAClickEvent),
    emitUserInteractionEvent: injectExperimentContext(emitUserInteractionEvent),
    emitAccordionToggleEvent: injectExperimentContext(emitAccordionToggleEvent),
  };
}
